import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { Button, Skeleton } from "@wedding/ui";
import { CheckCircle2, ClipboardCheck } from "lucide-react";
import { apiFetch } from "../api";
import { guestSides } from "../config";

type RsvpData = {
  mode: "invited" | "registration";
  name?: string;
  phone?: string;
  side?: string;
  projectName: string;
  brideName: string;
  groomName: string;
  weddingDate: string;
  city?: string;
  attendance?: string;
  invitationDelivery?: string;
  mailingAddress?: string;
  familyCount?: number;
  childrenCount?: number;
  specialMeal?: string;
};

type GuestRegistrationForm = {
  name: string;
  phone: string;
  side: string;
  attendance: "" | "出席" | "不出席";
  invitationDelivery: "" | "当面领取" | "邮寄";
  mailingAddress?: string;
  familyCount: number;
  childrenCount: number;
  specialMeal?: string;
  turnstileToken?: string;
};

function GuestRegistrationFormPage({
  token,
  info,
}: {
  token: string;
  info: RsvpData;
}) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<GuestRegistrationForm>({
    defaultValues: {
      name: info.name ?? "",
      phone: info.phone ?? "",
      side: info.side ?? "",
      attendance: ["出席", "不出席"].includes(String(info.attendance))
        ? (info.attendance as "出席" | "不出席")
        : "",
      invitationDelivery: ["当面领取", "邮寄"].includes(
        String(info.invitationDelivery),
      )
        ? (info.invitationDelivery as "当面领取" | "邮寄")
        : "",
      mailingAddress: info.mailingAddress ?? "",
      familyCount: Number(info.familyCount ?? 0),
      childrenCount: Number(info.childrenCount ?? 0),
      specialMeal: info.specialMeal ?? "",
    },
  });
  const [personalLink, setPersonalLink] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
  const delivery = watch("invitationDelivery");
  const attendance = watch("attendance");
  const familyCount = watch("familyCount");

  useEffect(() => {
    if (!siteKey) return;
    (
      window as Window & {
        onGuestRegistrationTurnstile?: (value: string) => void;
      }
    ).onGuestRegistrationTurnstile = (value: string) =>
      setValue("turnstileToken", value);
    if (!document.getElementById("turnstile-script")) {
      const script = document.createElement("script");
      script.id = "turnstile-script";
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
    return () => {
      delete (
        window as Window & {
          onGuestRegistrationTurnstile?: (value: string) => void;
        }
      ).onGuestRegistrationTurnstile;
    };
  }, [setValue, siteKey]);

  const submit = useMutation({
    mutationFn: (data: GuestRegistrationForm) =>
      apiFetch<{ personalToken: string }>(`/rsvp/${token}`, {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: (result) => {
      const nextLink = `${window.location.origin}/rsvp/${result.personalToken}`;
      setPersonalLink(nextLink);
    },
  });

  if (personalLink)
    return (
      <div className="rsvp-page">
        <div className="rsvp-card success">
          <CheckCircle2 />
          <span className="eyebrow">
            {info.mode === "invited" ? "修改成功" : "登记成功"}
          </span>
          <h1>谢谢，你的信息已经保存</h1>
          <p>请保存当前专属链接，以后再次打开即可查看并修改已填信息。</p>
          <div className="rsvp-success-actions">
            <Button onClick={() => window.location.assign(personalLink)}>
              查看 / 修改已填信息
            </Button>
            <Button
              className="secondary"
              onClick={async () => {
                await navigator.clipboard.writeText(personalLink);
                setLinkCopied(true);
              }}
            >
              {linkCopied ? "专属链接已复制" : "复制我的专属链接"}
            </Button>
          </div>
        </div>
      </div>
    );

  const weddingDate = new Intl.DateTimeFormat("zh-CN", {
    year: "numeric",
    month: "long",
    day: "numeric",
    weekday: "long",
    timeZone: "Asia/Shanghai",
  }).format(new Date(`${info.weddingDate}T00:00:00+08:00`));

  return (
    <div className="rsvp-page guest-registration-page">
      <div className="rsvp-card guest-registration-card">
        <span className="rsvp-mark">囍</span>
        <span className="eyebrow">
          {info.mode === "invited" ? "确认 / 修改宾客信息" : "宾客自助登记"}
        </span>
        <h1>
          {info.brideName}与{info.groomName}的婚礼
        </h1>
        <p>
          {weddingDate}
          {info.city ? ` · ${info.city}` : ""}
        </p>
        <div className="registration-intro">
          <ClipboardCheck size={20} />
          <span>
            {info.mode === "invited"
              ? "已自动带入上次保存的信息，确认或修改后再次提交即可。"
              : "请填写以下信息，提交后会直接加入新人的宾客名单。"}
          </span>
        </div>
        <form onSubmit={handleSubmit((data) => submit.mutate(data))}>
          <div className="form-grid">
            <label>
              <span>姓名 *</span>
              <input
                autoComplete="name"
                {...register("name", { required: "请填写姓名" })}
              />
              {errors.name && (
                <small className="field-error">{errors.name.message}</small>
              )}
            </label>
            <label>
              <span>手机号 *</span>
              <input
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                {...register("phone", {
                  required: "请填写手机号",
                  minLength: { value: 6, message: "请填写有效手机号" },
                  validate: (value) =>
                    value.replace(/\D/g, "").length >= 6 || "请填写有效手机号",
                })}
              />
              {errors.phone && (
                <small className="field-error">{errors.phone.message}</small>
              )}
            </label>
            <label>
              <span>归属方 *</span>
              <select {...register("side", { required: "请选择归属方" })}>
                <option value="">请选择</option>
                {guestSides.map((side) => (
                  <option key={side}>{side}</option>
                ))}
              </select>
              {errors.side && (
                <small className="field-error">{errors.side.message}</small>
              )}
            </label>
            <label>
              <span>是否出席 *</span>
              <select
                {...register("attendance", { required: "请选择是否出席" })}
              >
                <option value="">请选择</option>
                <option value="出席">出席</option>
                <option value="不出席">不出席</option>
              </select>
              {errors.attendance && (
                <small className="field-error">
                  {errors.attendance.message}
                </small>
              )}
            </label>
            <label className="span-2">
              <span>领取请柬方式 *</span>
              <select
                {...register("invitationDelivery", {
                  required: "请选择请柬领取方式",
                })}
              >
                <option value="">请选择</option>
                <option value="当面领取">当面领取</option>
                <option value="邮寄">邮寄</option>
              </select>
              {errors.invitationDelivery && (
                <small className="field-error">
                  {errors.invitationDelivery.message}
                </small>
              )}
            </label>
            {delivery === "邮寄" && (
              <label className="span-2">
                <span>邮寄地址 *</span>
                <textarea
                  rows={3}
                  autoComplete="street-address"
                  placeholder="请填写收件人、详细地址和邮编（如有）"
                  {...register("mailingAddress", {
                    required: "请填写邮寄地址",
                  })}
                />
                {errors.mailingAddress && (
                  <small className="field-error">
                    {errors.mailingAddress.message}
                  </small>
                )}
              </label>
            )}
            {attendance === "出席" && (
              <>
                <label>
                  <span>随行总人数（含儿童）</span>
                  <input
                    type="number"
                    min="0"
                    max="20"
                    {...register("familyCount", {
                      valueAsNumber: true,
                      min: 0,
                      max: 20,
                    })}
                  />
                </label>
                <label>
                  <span>其中儿童人数</span>
                  <input
                    type="number"
                    min="0"
                    max="20"
                    {...register("childrenCount", {
                      valueAsNumber: true,
                      min: 0,
                      max: 20,
                      validate: (value) =>
                        value <= Number(familyCount ?? 0) ||
                        "儿童人数不能超过随行总人数",
                    })}
                  />
                  {errors.childrenCount && (
                    <small className="field-error">
                      {errors.childrenCount.message}
                    </small>
                  )}
                </label>
                <p className="companion-note span-2">
                  成人随行人员会按“姓名的家属”单独加入宾客名单，方便安排座位；儿童只记录人数，暂不占座。
                </p>
              </>
            )}
            <label className="span-2">
              <span>特殊餐食要求</span>
              <textarea
                rows={3}
                placeholder="例如素食、清真、食物过敏；没有可留空"
                {...register("specialMeal")}
              />
            </label>
          </div>
          {siteKey && (
            <div
              className="cf-turnstile"
              data-sitekey={siteKey}
              data-callback="onGuestRegistrationTurnstile"
            />
          )}
          {submit.error && (
            <p className="form-error" role="alert">
              {submit.error.message}
            </p>
          )}
          <Button type="submit" disabled={submit.isPending}>
            {submit.isPending
              ? "正在保存…"
              : info.mode === "invited"
                ? "保存修改"
                : "提交登记"}
          </Button>
        </form>
      </div>
    </div>
  );
}

export function RsvpPage() {
  const { token } = useParams({ strict: false }) as { token: string };
  const query = useQuery({
    queryKey: ["rsvp", token],
    queryFn: () => apiFetch<RsvpData>(`/rsvp/${token}`),
    retry: false,
  });
  if (query.isLoading)
    return (
      <div className="rsvp-page">
        <Skeleton lines={8} />
      </div>
    );
  if (query.isError)
    return (
      <div className="rsvp-page">
        <div className="rsvp-card">
          <span className="rsvp-mark">囍</span>
          <h1>登记链接暂不可用</h1>
          <p>请联系新人确认链接是否正确。</p>
        </div>
      </div>
    );
  return <GuestRegistrationFormPage token={token} info={query.data!} />;
}
